import type { RefObject } from 'react';
import { Component, createRef } from 'react';

import { BREAKPOINTS } from '@skyscanner/backpack-web/bpk-component-breakpoint';
import BpkPanel from '@skyscanner/backpack-web/bpk-component-panel';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import Close from './Close';
import InformationHeader from './InformationHeader';
import MobilePanel from './MobilePanel';
import PanelBody from './PanelBody';
import PanelHeader from './PanelHeader';
import PriceCta from './PriceCta';

import type { PoiDataState } from '@hotels-hotel-detail/data-access/types';
import type { Bounds } from '@shared/types/frontend/google-maps';
import type {
  PoiData,
  PoiDataArray,
} from '@shared/types/frontend/nearby-transport-response';
import type { Coordinates } from '@shared/types/google-maps';
import type { HotelContent } from '@shared/types/hotel-content';
import type { I18nService } from '@shared/types/i18n';
import type { EntityId, Nullable } from '@shared/types/utils';

import STYLES from './DetailedFullMap.module.scss';

import PoiMarker from '@hotels-hotel-detail/ui/PoiMarker';
import {
  THING_TO_DO,
  TRANSPORT,
} from '@hotels-hotel-detail/utils/location-constants';
import withBpkBreakpoint from '@shared/ui/common/BpkBreakpoint/withBpkBreakpoint';
import HotelMapMarker from '@shared/ui/components/HotelMapMarker';
import MapWrapper from '@shared/ui/components/MapWrapper';
import { withI18n } from '@shared/ui/contexts/i18n';
import { getHotelCoordinates, getBounds } from '@shared/utils-frontend/hotel';
import { getInnerWidth } from '@shared/utils-frontend/window';

const cls = cssModules(STYLES);

const PANEL_MARGIN_LEFT = 24;
const PANEL_WIDTH = 500;
const MOBILE_TRANSITION_TIME = 500;
const MIN_SELECCED_ZOOM = 12;
type Props = {
  uBFFHotel: HotelContent;
  categories: string[];
  i18n: I18nService;
  isMobile?: boolean;
  onClose: Function;
} & PoiDataState;

type State = {
  isMobilePanelMax: boolean;
};

type PoiV2ReducerShape = {
  [key: string]: PoiData;
};
class DetailedFullMap extends Component<Props, State> {
  cardRefs: any;

  zoom?: number;

  mapRef: any;

  listContainer: any;

  mobilePanelRef: RefObject<any>;

  lngOffset: number;

  hotelCoordinates: Coordinates | { latitude: null; longitude: null };

  selectedCoordinates: Nullable<Coordinates>;

  constructor(props: Props) {
    super(props);

    const { uBFFHotel } = props;
    this.cardRefs = {};
    this.mapRef = createRef();
    this.listContainer = createRef();
    this.mobilePanelRef = createRef();
    this.hotelCoordinates = getHotelCoordinates(uBFFHotel);
    this.lngOffset = 0;
    this.selectedCoordinates = null;
    this.state = {
      isMobilePanelMax: false,
    };
  }

  componentDidMount() {
    this.scrollToCard(this.props.selectedEntityId);
  }

  componentWillUnmount() {
    const { onPoiMarkerClick, onTabClick } = this.props;
    onPoiMarkerClick(null);
    onTabClick(THING_TO_DO);
  }

  onMobilePanelTouchEnd = (isMobilePanelMax: boolean) => {
    this.setState({
      isMobilePanelMax,
    });
  };

  getShouldZoom = (entityId: Nullable<EntityId>) => {
    const { isMobile, transportWithoutAirport } = this.props;
    const { zoom } = this;
    const transportWithoutAirportMap = transportWithoutAirport
      ?.slice(1)
      .reduce((acc: PoiV2ReducerShape, cur: PoiData) => {
        acc[cur.entityId] = cur;
        return acc;
      }, {});
    return !!(
      isMobile &&
      zoom &&
      zoom < MIN_SELECCED_ZOOM &&
      entityId &&
      transportWithoutAirportMap &&
      transportWithoutAirportMap[entityId]
    );
  };

  onPoiMarkerClick = (
    entityId: Nullable<EntityId>,
    selectedCoordinates: Nullable<Coordinates>,
  ) => {
    this.props.onPoiMarkerClick(entityId);
    this.selectedCoordinates = selectedCoordinates;
    if (
      this.mobilePanelRef &&
      this.mobilePanelRef.current &&
      this.mobilePanelRef.current.resetNormalHeight
    ) {
      this.mobilePanelRef.current.resetNormalHeight();
    }
    const shouldZoom = this.getShouldZoom(entityId);
    if (shouldZoom) {
      this.onZoom(MIN_SELECCED_ZOOM);
    }
    this.move(shouldZoom);
  };

  getPoiMarkers(poiDatas?: PoiDataArray) {
    const { selectedEntityId } = this.props;
    return poiDatas?.map((poiData: PoiData) => {
      const { entityId, latitude, longitude, poiName, poiType } = poiData;
      const isSelected = entityId === selectedEntityId;
      return (
        <PoiMarker
          key={entityId}
          poi={{
            latitude,
            longitude,
            type: poiType,
            name: poiName,
          }}
          className={cls(isSelected && 'DetailedFullMap__selectedMarker')}
          selected={isSelected}
          onClick={() => {
            this.onPoiMarkerClick(entityId, { latitude, longitude });
            this.scrollToCard(entityId);
          }}
        />
      );
    });
  }

  getMarkers() {
    const { airport, nearbyPoi, selectedTab, transportWithoutAirport } =
      this.props;
    if (selectedTab === THING_TO_DO) {
      return this.getPoiMarkers(nearbyPoi);
    }
    if (selectedTab === TRANSPORT && transportWithoutAirport) {
      return this.getPoiMarkers(airport?.concat(transportWithoutAirport));
    }
    return null;
  }

  move = (shouldZoom: boolean) => {
    const { isMobile } = this.props;
    const { selectedCoordinates } = this;
    if (!this.mapRef || !selectedCoordinates) {
      return;
    }
    const moveDelay = isMobile ? MOBILE_TRANSITION_TIME : 0;
    setTimeout(() => {
      const shouldMove = this.getShouldMove();
      if (shouldZoom || shouldMove) {
        this.mapRef.panTo({
          lat: selectedCoordinates.latitude,
          lng: selectedCoordinates.longitude - 0.5 * this.lngOffset,
        });
      }
    }, moveDelay);
  };

  scrollToCard = (id: Nullable<string>) => {
    if (id && this.cardRefs[id] && this.cardRefs[id].scrollIntoView) {
      this.cardRefs[id].scrollIntoView();
    }
  };

  cardRefCallback = (el: HTMLDivElement, id: EntityId) => {
    this.cardRefs[id] = el;
  };

  listContainerRefCb = (el: HTMLDivElement) => {
    this.listContainer = el;
  };

  mapRefCb = (ref: any, bounds: Bounds) => {
    this.mapRef = ref;
    const { selectedCoordinates } = this;
    const { isMobile } = this.props;
    if (!selectedCoordinates && ref && bounds && !isMobile) {
      ref.fitBounds(bounds, {
        left: PANEL_WIDTH + PANEL_MARGIN_LEFT,
      });
    }
  };

  getBounds = () => {
    const { airport, nearbyPoi, selectedTab, transportWithoutAirport } =
      this.props;
    let selectedPoints;
    if (selectedTab === THING_TO_DO) {
      selectedPoints = nearbyPoi;
    }
    if (selectedTab === TRANSPORT && transportWithoutAirport) {
      selectedPoints = airport?.concat(transportWithoutAirport);
    }

    if (!selectedPoints) {
      return null;
    }

    const center = this.hotelCoordinates as Coordinates;

    const coordinates: Coordinates[] = selectedPoints.map((poi) => ({
      latitude: poi.latitude,
      longitude: poi.longitude,
    }));

    return getBounds([...coordinates, center]);
  };

  setLngOffset = () => {
    if (this.props.isMobile) {
      return;
    }
    if (
      this.mapRef &&
      this.mapRef.getBounds &&
      this.mapRef.getBounds() &&
      window
    ) {
      const bounds = this.mapRef.getBounds().toJSON();
      const { east, west } = bounds;
      const innerWidth = getInnerWidth();
      this.lngOffset =
        ((PANEL_WIDTH + PANEL_MARGIN_LEFT) * Math.abs(east - west)) /
        innerWidth;
    }
  };

  onZoom = (zoom: number) => {
    this.zoom = zoom;
    this.setLngOffset();
  };

  getShouldMove = () => {
    const { selectedCoordinates } = this;
    if (
      this.mapRef &&
      this.mapRef.getBounds &&
      this.mapRef.getBounds() &&
      selectedCoordinates
    ) {
      const bounds = this.mapRef.getBounds().toJSON();
      const { latitude, longitude } = selectedCoordinates;
      const { east, north, south, west } = bounds;
      if (
        longitude > east ||
        longitude < west + this.lngOffset ||
        latitude > north ||
        latitude < south
      ) {
        return true;
      }
    }
    return false;
  };

  getCenter = () => {
    if (!this.mapRef || !this.mapRef.getCenter) {
      return this.selectedCoordinates;
    }
    const shouldMove = this.getShouldMove();
    const { selectedCoordinates } = this;
    if (
      shouldMove &&
      selectedCoordinates?.longitude &&
      selectedCoordinates.latitude
    ) {
      return {
        longitude: selectedCoordinates.longitude - 0.5 * this.lngOffset,
        latitude: selectedCoordinates.latitude,
      };
    }
    const center = this.mapRef.getCenter();
    if (!center) {
      return this.selectedCoordinates;
    }
    const { lat, lng } = center.toJSON();
    return {
      latitude: lat,
      longitude: lng,
    };
  };

  onTabClick = (categoriesName: string) => {
    this.props.onTabClick(categoriesName);
    this.onPoiMarkerClick(null, null);
    if (this.listContainer && this.listContainer.scrollTo) {
      this.listContainer.scrollTo(0, 0);
    }
  };

  render() {
    const {
      airport,
      categories,
      i18n,
      isMobile,
      nearbyPoi,
      onClose,
      selectedEntityId,
      selectedTab,
      transportWithoutAirport,
      uBFFHotel,
    } = this.props;
    const {
      gallery: { images },
      hotelId,
      location: { address },
      name,
      stars,
    } = uBFFHotel;
    const { selectedCoordinates, zoom } = this;
    const { isMobilePanelMax } = this.state;
    const bounds = this.getBounds();

    const mainImg = images[0]?.url;
    const panelHeader = (
      <PanelHeader
        onTabClick={this.onTabClick}
        categories={categories}
        selectedTab={selectedTab}
      />
    );
    const panelBody = (
      <PanelBody
        selectedTab={selectedTab}
        nearbyPoi={nearbyPoi}
        airport={airport}
        transportWithoutAirport={transportWithoutAirport}
        selectedEntityId={selectedEntityId}
        onPoiMarkerClick={this.onPoiMarkerClick}
        cardRefCb={this.cardRefCallback}
        listContainerRefCb={this.listContainerRefCb}
        hotelID={hotelId}
      />
    );

    return (
      <section
        aria-label={i18n.translate('Explore_entity_by_map', {
          entityName: name,
        })}
        className={STYLES.DetailedFullMap}
      >
        {isMobilePanelMax ? null : <Close onClose={onClose} />}
        {bounds && (
          <MapWrapper
            showControls={!isMobile}
            greedyGestureHandling
            dynamic
            className={STYLES.DetailedFullMap__map}
            mapRef={(ref: any) => {
              this.mapRefCb(ref, bounds);
            }}
            bounds={selectedCoordinates ? null : bounds}
            onZoom={this.onZoom}
            zoom={selectedCoordinates ? zoom : undefined}
            center={selectedCoordinates ? this.getCenter() : null}
          >
            {isMobilePanelMax ? (
              <div className={STYLES.DetailedFullMap__mobileMapWrapper} />
            ) : null}
            {this.hotelCoordinates.latitude &&
            this.hotelCoordinates.longitude ? (
              <HotelMapMarker position={this.hotelCoordinates} selected />
            ) : null}

            {this.getMarkers()}
          </MapWrapper>
        )}

        {isMobile ? (
          <>
            <MobilePanel
              ref={this.mobilePanelRef}
              header={panelHeader}
              body={panelBody}
              onTouchEnd={this.onMobilePanelTouchEnd}
              transitionTime={MOBILE_TRANSITION_TIME}
            />
            <div className={STYLES.DetailedFullMap__ctaContainer}>
              <PriceCta onClose={onClose} />
            </div>
          </>
        ) : (
          <BpkPanel padded={false} className={STYLES.DetailedFullMap__panel}>
            <InformationHeader
              onClose={onClose}
              name={name}
              mainImg={mainImg}
              numberOfStars={Number(stars.level)}
              address={address}
            />
            {panelHeader}
            {panelBody}
          </BpkPanel>
        )}
      </section>
    );
  }
}

export const RawDetailedFullMap = DetailedFullMap;

export default withI18n(
  withBpkBreakpoint(DetailedFullMap, BREAKPOINTS.MOBILE, 'isMobile'),
);
